import React from 'react';
import { Mail, Tag, ChevronDown, ChevronRight } from 'lucide-react';
import { SYSTEM_FOLDERS, formatAccountLabel, accountKey, accountHeaderValue } from './emailUtils';

/**
 * EmailSidebar — left rail of the Email page.
 *
 * Pure presentational component. All state is owned by `pages/Email.jsx`
 * and passed down as props. Extracted from the 1200-line Email.jsx monolith
 * (Vague 113 Wave 1u3 refactor step 1) to improve testability + reduce
 * re-renders on the main page.
 *
 * Props:
 *  - accounts: array of mailbox accounts (from /api/mailbox/status)
 *  - selectedAccount: current dropdown value
 *  - onSelectAccount(value, account): callback when user switches account
 *  - folder: current selected folder/label id
 *  - onSelectFolder(id): change folder
 *  - labels: user labels (Gmail/IMAP/Outlook)
 *  - statusEmail: primary account email shown at the bottom
 *  - onCompose(): open the compose dialog
 */
export default function EmailSidebar({
  accounts = [],
  selectedAccount,
  onSelectAccount,
  folder,
  onSelectFolder,
  labels = [],
  statusEmail,
  onCompose,
  folderCounts = {},
  onResetWidths,
  labelsCollapsed = false,
  onToggleLabels,
}) {
  const handleAccountChange = (e) => {
    const val = e.target.value;
    // Wave 1u5 — special "all accounts" option: clear override + signal parent
    if (val === '__all__') {
      sessionStorage.removeItem('ifaap_mailbox_account');
      sessionStorage.setItem('ifaap_mailbox_all', '1');
      onSelectAccount?.(val, { provider: 'all', email: 'Toutes les boîtes' });
      return;
    }
    sessionStorage.removeItem('ifaap_mailbox_all');
    const acct = accounts.find((a) => accountKey(a, 0).startsWith(a.provider + ':') && (
      (a.shared_config_id && val === `${a.provider}:${a.shared_config_id}`) ||
      (!a.shared_config_id && val === `${a.provider}:${a.email || ''}`)
    )) || accounts.find((a, idx) => accountKey(a, idx) === val);
    const headerVal = accountHeaderValue(acct);
    if (headerVal) {
      sessionStorage.setItem('ifaap_mailbox_account', headerVal);
    } else {
      sessionStorage.removeItem('ifaap_mailbox_account');
    }
    onSelectAccount?.(val, acct);
  };

  const userLabels = labels.filter((l) => l.type === 'user');

  return (
    <aside className="bg-[#FAFAFE] border-r border-[#EFF0F6] flex flex-col h-full w-full" data-testid="email-sidebar">
      <div className="p-3 border-b border-[#EFF0F6]">
        {accounts.length > 1 && (
          <div className="mb-2">
            <label className="block text-[10.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1">Compte actif</label>
            <select
              value={selectedAccount || ''}
              onChange={handleAccountChange}
              data-testid="email-account-selector"
              className="w-full h-8 px-2 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12px]"
            >
              {/* Wave 1u5 — combined view across every connected mailbox.
                  Note: keep text content as a single string (no spans/icons inside)
                  to avoid React hydration warnings about <span> in <option>. */}
              <option value="__all__">{`📥 Toutes les boîtes (${accounts.length})`}</option>
              {accounts.map((a, idx) => (
                <option key={accountKey(a, idx)} value={accountKey(a, idx)}>
                  {formatAccountLabel(a)}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-[#8E8FA5] mt-1">
              {selectedAccount === '__all__' ? 'Vue agrégée — utilisez le sélecteur pour envoyer depuis une boîte précise.' : 'Les envois utilisent les paramètres de ce compte.'}
            </div>
          </div>
        )}
        <button
          onClick={onCompose}
          data-testid="email-compose-btn"
          title="Nouveau message"
          className="w-full h-10 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center justify-center gap-2 hover:opacity-90"
        >
          <Mail className="w-4 h-4" /> Composer
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        {SYSTEM_FOLDERS.map((f) => {
          const Icon = f.icon;
          const active = folder === f.id;
          const c = folderCounts[f.id] || {};
          const unread = c.unread || 0;
          const total = c.total || 0;
          // Wave 1u4 — show unread badge with count; fall back to total for Drafts/Sent/Trash
          const showUnreadBadge = unread > 0 && (f.id === 'INBOX' || f.id === 'STARRED' || f.id === 'SPAM');
          const showTotalBadge = !showUnreadBadge && (f.id === 'DRAFT' || f.id === 'SNOOZED') && total > 0;
          return (
            <button
              key={f.id}
              onClick={() => onSelectFolder?.(f.id)}
              data-testid={`email-folder-${f.id.toLowerCase()}`}
              className={`w-full text-left flex items-center gap-3 px-4 py-2 text-[12.5px] font-medium ${active ? 'bg-[#EEF2FF] text-[#4F46E5] border-l-2 border-[#4F46E5]' : 'text-[#4A4B63] hover:bg-[#F3F4F8]'}`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 truncate">{f.label}</span>
              {showUnreadBadge && (
                <span
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#4F46E5] text-white text-[10px] font-bold leading-none"
                  data-testid={`email-folder-badge-${f.id.toLowerCase()}`}
                  title={`${unread} non lu${unread > 1 ? 's' : ''}`}
                >
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
              {showTotalBadge && (
                <span
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#EFF0F6] text-[#6B6C85] text-[10px] font-bold leading-none"
                  data-testid={`email-folder-badge-${f.id.toLowerCase()}`}
                >
                  {total > 99 ? '99+' : total}
                </span>
              )}
            </button>
          );
        })}
        {userLabels.length > 0 && (
          <>
            {/* Wave 1u6 — Collapsible "Mes libellés" header. Persists the open
                state via /api/user/email-layout.labels_collapsed so each user
                keeps their own preference. */}
            <button
              type="button"
              onClick={() => onToggleLabels?.(!labelsCollapsed)}
              data-testid="email-labels-toggle"
              title={labelsCollapsed ? 'Afficher les libellés' : 'Masquer les libellés'}
              className="w-full px-4 mt-3 mb-1 text-[10.5px] font-bold uppercase tracking-wide text-[#8E8FA5] flex items-center gap-1 hover:text-[#4F46E5]"
            >
              {labelsCollapsed
                ? <ChevronRight className="w-3 h-3" />
                : <ChevronDown className="w-3 h-3" />}
              <span>Mes libellés ({userLabels.length})</span>
            </button>
            {!labelsCollapsed && userLabels.slice(0, 20).map((l) => (
              <button
                key={l.id}
                onClick={() => onSelectFolder?.(l.id)}
                className={`w-full text-left flex items-center gap-3 px-4 py-1.5 text-[12px] ${folder === l.id ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85] hover:bg-[#F3F4F8]'}`}
              >
                <Tag className="w-3 h-3" />
                <span className="truncate">{l.name}</span>
              </button>
            ))}
          </>
        )}
      </nav>

      <div className="px-3 py-2 border-t border-[#EFF0F6] text-[11px] text-[#8E8FA5] flex items-center justify-between gap-2">
        <span className="truncate" title={statusEmail}>{statusEmail}</span>
        {onResetWidths && (
          <button
            type="button"
            onClick={onResetWidths}
            data-testid="email-reset-widths"
            title="Réinitialiser la largeur des colonnes"
            className="text-[10px] text-[#6B6C85] hover:text-[#4F46E5] underline whitespace-nowrap"
          >
            Reset largeur
          </button>
        )}
      </div>
    </aside>
  );
}
